import React, { Children, useEffect } from 'react';
import PropTypes from 'prop-types';
import Scrollspy from 'react-scrollspy';
import { FilterButton } from './';
import { track } from '../modules/tracking';
import { TRACE } from '../modules/constants';

import { Toggle } from '.';

const motion =
    typeof window !== 'undefined' &&
    window.matchMedia &&
    (window.matchMedia('(prefers-reduced-motion: no-preference)') || {})
        .matches;

let Link;

if (motion) {
    Link = require('react-anchor-link-smooth-scroll').default;
}

const COMPONENT_CLASS_NAME = 'subNav';
const INNER_CLASS_NAME = 'inner';
const ACTIVE_CLASS_NAME = 'active';
const NAV_CONTAINER_CLASS_NAME = 'navItems';

let startPosition;
let nav;

/**
 * Get leftmost position of the active menu item.
 *
 * @function
 * @param    {HTMLElement} elem selected link
 * @param    {string} sel  optional selector. If not set, '.active' is used.
 * @returns  {number}      offset left.
 */
function getActiveLeft(elem, sel) {
    return (elem.querySelector(sel || '.active') || {}).offsetLeft;
}

/**
 * Realign the subnavigation with the selected item on the left of the page.
 *
 * @function
 */
function realign() {
    if (!nav) {
        nav = document.querySelector(`.${this.className}`);
    }

    if (!startPosition) {
        startPosition = getActiveLeft(nav, 'a');
    }

    const selected = getActiveLeft(nav);

    if (!selected || !startPosition || !nav) {
        return;
    }

    const scrollDistance = selected - startPosition;

    if (typeof nav.scrollTo !== 'undefined') {
        nav.scrollTo({
            top: 0,
            left: scrollDistance,
            behavior: motion ? 'smooth' : 'auto'
        });
    }
}

/**
 * Reset menu item (called on resize/change orientation of page).
 *
 * @function
 */
function reset() {
    startPosition = null;
}

/**
 * [Navigation description]
 *
 * @function
 * @param       {number} filterCount    useState variable: amount of filters selected.
 * @param       {array}  sections       sections array from the API.
 * @param       {function} setShade    useState function to set the shade behind the filters on and off.
 * @param       {function} setShowFilter useState function. Used to control opening and closing of filters component.
 * @param       {[type]} showFilter     [description]
 * @constructor
 */
function Navigation({
    filterCount,
    sections,
    setShade,
    setShowFilter,
    showFilter,
    allergenFilter,
    setAllergenFilter
}) {
    const filterProps = {
        filterCount,
        setShade,
        setShowFilter,
        showFilter
    };

    if (!sections || !sections.length) {
        return null;
    }

    const sectionIDs = sections.map(({ id }) => {
        return (id || '').replace(/:/g, '_');
    });

    useEffect(() => {
        if (typeof document === 'undefined') {
            return;
        }

        document.addEventListener('resize', reset, false);
        document.addEventListener('orientationchange', reset, false);

        return () => {
            document.removeEventListener('resize', reset, false);
            document.removeEventListener('orientationchange', reset, false);
        };
    });

    function afterClick(e) {
        const hash = (e.target.href || '').split('#')[1];
        const name = e.target.innerHTML;

        track(`Main menu: Click: Jump page to: ${name}`)();

        const target = document.getElementById(hash);

        if (target) {
            const anchor = target.querySelector('button');
            if (anchor && anchor.focus) {
                if (motion) {
                    setTimeout(() => {
                        anchor.focus();
                    }, 750);
                } else {
                    anchor.focus();
                }
            }
        }
    }

    function formatKey(key) {
        return key
            .toLowerCase()
            .split('_')
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    }

    // Inside your component or functional component's scope
    function handleFilterChange(key, state) {
        const update = {};
        update[key] = state;
        setAllergenFilter({
            ...allergenFilter,
            ...update
        });
    }
    return (
        <div className='navContainer'>
            <nav className={COMPONENT_CLASS_NAME}>
                <div className={INNER_CLASS_NAME}>
                    <Scrollspy
                        items={sectionIDs}
                        className={NAV_CONTAINER_CLASS_NAME}
                        componentTag='div'
                        currentClassName={ACTIVE_CLASS_NAME}
                        offset={-200}
                        onUpdate={realign}
                    >
                        {sections.map(({ id, displayName }, idx) => {
                            const mid = (id || '').replace(/:/g, '_');

                            if (motion) {
                                return (
                                    <Link
                                        onClick={afterClick}
                                        key={idx}
                                        href={`#${mid}`}
                                    >
                                        {displayName}
                                    </Link>
                                );
                            }

                            return (
                                <a
                                    onClick={afterClick}
                                    key={idx}
                                    href={`#${mid}`}
                                >
                                    {displayName}
                                </a>
                            );
                        })}
                    </Scrollspy>
                </div>
            </nav>

            <div className='filterParent'>
                <div className='filterContainer'>
                    <div className='filterdItemContainer'>
                        <FilterButton {...filterProps} />

                        <div className='filteredItems'>
                            {Object.entries(allergenFilter).map(
                                ([key, value]) => {
                                    if (
                                        value === true &&
                                        key !== "HIDETRACES"
                                    ) {
                                        const formattedKey = formatKey(key);

                                        return (
                                            <div
                                                key={key}
                                                className='filterItem'
                                            >
                                                <button
                                                    onClick={() =>
                                                        handleFilterChange(
                                                            key,
                                                            false
                                                        )
                                                    }
                                                >
                                                    X
                                                </button>
                                                <span>{formattedKey}</span>
                                            </div>
                                        );
                                    }
                                    return null;
                                }
                            )}
                        </div>
                    </div>

                    <div className='filterToggle'>
                        {Object.values(allergenFilter).some(
                            (value) => value === true
                        ) && (
                            <label
                                className='toggleTrace'
                                htmlFor='toggleTrace'
                            >
                                <input
                                    id='toggleTrace'
                                    name='hideTraces'
                                    type='checkbox'
                                    onChange={(e) => {
                                        handleFilterChange(
                                            "HIDETRACES",
                                            e.target.checked
                                        );
                                    }}
                                    checked={allergenFilter.HIDETRACES}
                                />
                                {TRACE}
                                <Toggle type='yn' />
                            </label>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

Navigation.propTypes = {
    filterCount: PropTypes.number,
    sections: PropTypes.array,
    setShade: PropTypes.func,
    setShowFilter: PropTypes.func,
    showFilter: PropTypes.bool
};

export { Navigation };
